
import { Component, Emit, Prop, Vue } from "vue-property-decorator";
import vSelect3 from "vselect3";
import { UserPermissions } from "@/store/types";
import { Getter } from "vuex-class";
import { cloneDeep, debounce } from "lodash";
import { getDebouncePeriod } from "@/utils/debounce-helper";
import NotificationTemplate from "@/types/sv-data/notification-templates/NotificationTemplate";
import notificationTemplatesApi from "@/services/api.notificationTemplate.service";
import NotificationTemplateTypeId from "@/types/sv-data/enums/NotificationTemplateTypeIds";

@Component({
	components: {
		"v-select-3": vSelect3,
	}
})
export default class NotificationTemplatesSelect extends Vue {

	@Prop({ type: Number, default: null })
	private value: number | null;

    @Prop({ type: Boolean, default: false })
    private readOnly: boolean;

    @Prop({ type: String, default: "Attach Notification Template" })
    private label: string;

    @Prop({ type: Boolean })
    private required: boolean;

    @Prop({ type: String, default: "Select Notification Template"})
    private placeholder: string;

	@Prop({ type: Array })
	private notificationTemplates: NotificationTemplate[];

	@Getter("getPermissions") private permissions: UserPermissions;
	@Getter("getFeature") getFeature: (featureName: string[]) => boolean;
	@Getter getUserTenantGroupId: number;
    
	private loadedNotificationTemplates: NotificationTemplate[] = [];
	private selectedNotificationTemplate: NotificationTemplate = null;
    
	private get isNotificationTemplatesEnabled(): boolean {
		return this.getFeature(["Alarms", "SiteMonitor", "Email", "NotificationTemplates"]);
	}

	private get hasNotificationTemplatePermissions(): boolean {
		return this.permissions.canViewNotificationTemplates || this.permissions.canEditNotificationTemplates;
	}
    
    private async mounted(): Promise<void> {
		await this.setNotificationTemplates();
		this.setSelectedNotificationTemplate();
    }
    
	private handleSearchInput = debounce(async (searchTerm: string): Promise<void> => {
			await this.loadNotificationTemplates(searchTerm);
		},
		getDebouncePeriod(),
		{ leading: false, trailing: true }
    );

	private async setNotificationTemplates(): Promise<void> {
		if (this.notificationTemplates && this.notificationTemplates.length > 0) {
			this.loadedNotificationTemplates = cloneDeep(this.notificationTemplates);
		} else {
			await this.loadNotificationTemplates();
		}
	}
    
	private async loadNotificationTemplates(searchTerm: string = ""): Promise<void> {
		if (!this.isNotificationTemplatesEnabled || !this.hasNotificationTemplatePermissions) {
			return;
		}

		try {
			this.loadedNotificationTemplates = await notificationTemplatesApi.getAllNotificationTemplates(NotificationTemplateTypeId.Event, this.getUserTenantGroupId);
		} catch (ex) {
			console.error("Unexpected error fetching notification templates: " + ex);
		}
	}

	private setSelectedNotificationTemplate(): void {
		if (this.value) {
			this.selectedNotificationTemplate = 
				this.loadedNotificationTemplates.filter((template: NotificationTemplate) => template.notificationTemplateId === this.value)[0];
		}
	}

	private onSearch(searchTerm: string): void {
		this.handleSearchInput(searchTerm);
	}

    @Emit("input")
    private onNotificationTemplateUpdated(notificationTemplate: NotificationTemplate): number {
        this.selectedNotificationTemplate = notificationTemplate;
        return notificationTemplate?.notificationTemplateId;
    }
}
